<template>
  <DropdownMenuRoot
    v-if="channelStore.permissions.manageShows"
    v-model:open="isOpen"
  >
    <div
      class="w-8 h-8 bg-brand-bgdivider-20 rounded-[100px] backdrop-blur-[35px] justify-center items-center gap-2 flex"
    >
      <div class="relative flex items-center justify-center">
        <DropdownMenuTrigger :aria-label="$t('channel.shows.dropdown.aria')">
          <Icon
            icon="ph:dots-three"
            class="text-basics-secondary-80"
            width="24"
            height="24"
          />
        </DropdownMenuTrigger>
      </div>
    </div>

    <DropdownMenuPortal>
      <DropdownMenuContent
        class="outline-none shadow bg-brand-bgdivider-20 text-brand-tertiary-60 backdrop-blur-3xl p-2 rounded-xl will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade z-[999999999]"
        :side-offset="5"
      >
        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-brand-primary text-brand-primary data-[highlighted]:text-basics-primary rounded-md px-2 py-2.5 gap-2"
          @click="onEditShowClick"
        >
          <Icon icon="material-symbols:edit" width="24" height="24" />
          {{ $t("channel.shows.dropdown.edit") }}
        </DropdownMenuItem>

        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-brand-primary text-brand-primary data-[highlighted]:text-basics-primary rounded-md px-2 py-2.5 gap-2"
          @click="onTogglePinClick"
        >
          <Icon
            :icon="isPinned ? 'mdi:pin-off' : 'mdi:pin'"
            width="24"
            height="24"
          />
          {{
            isPinned
              ? $t("channel.shows.dropdown.unpin")
              : $t("channel.shows.dropdown.pin")
          }}
        </DropdownMenuItem>

        <DropdownMenuItem
          v-if="show.status !== ShowStatus.Live"
          class="flex items-center select-none outline-none data-[highlighted]:bg-brand-primary data-[highlighted]:text-basics-primary text-basics-accent-red rounded-md px-2 py-2.5 gap-2"
          @click="deleteConfirmation = true"
        >
          <Icon icon="ph:trash" width="24" height="24" />
          {{ $t("channel.shows.dropdown.delete") }}
        </DropdownMenuItem>

        <DropdownMenuArrow class="fill-brand-bgdivider-20 backdrop-blur-3xl" />
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>

  <DangerConfirmationModal
    v-model="deleteConfirmation"
    :title="$t('channel.shows.confirmations.delete.title')"
    :confirm-label="$t('channel.shows.confirmations.delete.confirm')"
    @confirm="onDeleteShow"
  />
</template>

<script setup lang="ts">
import { ShowStatus, type Show, DeleteShowDocument } from "@/generated/graphql";
import { RouteNames } from "@/router/app.router";
import { useChannelStore } from "@/stores/channel.store";
import { Icon } from "@iconify/vue";
import { useRouter } from "vue-router";

import {
  DropdownMenuArrow,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "reka-ui";
import { ref, defineAsyncComponent } from "vue";
import { useMutation } from "@vue/apollo-composable";
import { useCleanupCache } from "@/composables/global/use-cleanup-cache";

// Lazy load
const DangerConfirmationModal = defineAsyncComponent(
  () => import("@/components/show/modals/danger-confirmation-modal.vue"),
);

const router = useRouter();
const channelStore = useChannelStore();
const { cleanupCache } = useCleanupCache();

const props = defineProps<{
  show: Show;
  isPinned?: boolean;
}>();

const emit = defineEmits<{
  togglePin: [];
}>();

const deleteConfirmation = ref(false);
const isOpen = ref(false);

const onEditShowClick = () => {
  router.push({
    name: RouteNames.NewShow,
    params: {
      show: props.show.id,
      channel: channelStore.channelSlug,
      type: props.show.status === ShowStatus.VideoCast ? "upload" : "live",
    },
  });
};

const { mutate: deleteShow } = useMutation(DeleteShowDocument);

const onDeleteShow = async () => {
  const result = await deleteShow({
    id: props.show.id,
  });
  if (result?.data?.deleteShow) {
    await cleanupCache();
  }
};

const onTogglePinClick = () => {
  emit("togglePin");
};
</script>
